/**
 * Where each story target sits in its painted plate (uv, origin top-left), so a played card can fly to
 * and stamp the right spot, and the target gets a name pin in the scene. Hand-placed from the calm
 * plates; anything missing falls back to a sensible spot for its beat type.
 */
import type { BeatType } from '@plotjack/core';
import { figureAnchor } from './figures';

export type Uv = [number, number];

const A: Record<string, Record<string, Uv>> = {
  s01_gate: { hero: [0.33, 0.6], gatekeeper: [0.73, 0.53], whisper_gate: [0.66, 0.35], candle_smoke: [0.5, 0.22] },
  s02_archive: { archive: [0.45, 0.28], villain: [0.68, 0.4], macguffin: [0.6, 0.44], trade: [0.55, 0.47] },
  s03_bard: { bard: [0.6, 0.42], stair: [0.6, 0.78], ballad: [0.48, 0.3], tower_mood: [0.38, 0.22] },
  s04_tower: { tower: [0.55, 0.22], hero: [0.36, 0.42], windows: [0.85, 0.2], climb: [0.48, 0.72] },
  s05_market: { market: [0.5, 0.28], extras: [0.1, 0.4], riot: [0.55, 0.5], noise: [0.62, 0.25] },
  s06_bridge: { bridge: [0.68, 0.6], chasm: [0.5, 0.85], macguffin: [0.38, 0.34], choice: [0.55, 0.58] },
  s07_throne: { throne: [0.7, 0.4], villain: [0.68, 0.45], macguffin: [0.62, 0.24], coronation: [0.6, 0.52] },
  s08_reveal: { hero: [0.33, 0.65], strings: [0.6, 0.2], glitch: [0.75, 0.45], panic: [0.5, 0.35] },
  s09_oath_protocol: { oath: [0.48, 0.28], hero: [0.36, 0.6], gatekeeper: [0.6, 0.55], protocol_mood: [0.72, 0.3] },
  s10_tide_lock: { tide: [0.75, 0.72], scriptorium: [0.6, 0.3], scribe: [0.52, 0.58], damp: [0.82, 0.86] },
  s11_flood_shelves: { shelves: [0.2, 0.35], hero: [0.39, 0.52], prophecy_cards: [0.55, 0.45], chapter_title: [0.65, 0.15] },
  s12_scribe_pell: { scribe: [0.75, 0.33], ledger: [0.72, 0.56], hero: [0.42, 0.45], ink_bribe: [0.55, 0.5] },
  s13_margin_knight: { knight: [0.5, 0.42], margin_road: [0.65, 0.72], hero: [0.37, 0.55], spacing: [0.2, 0.2] },
  s14_ledger_trial: { trial: [0.67, 0.52], villain: [0.68, 0.4], macguffin: [0.6, 0.44], objection: [0.4, 0.3] },
  s15_bard_reprise: { bard: [0.6, 0.42], undertow: [0.6, 0.8], hero: [0.4, 0.7], romantic_mood: [0.7, 0.25] },
  s16_ink_whale: { whale: [0.58, 0.32], macguffin: [0.38, 0.48], hero: [0.28, 0.6], song: [0.72, 0.2] },
  s17_dam_burst: { dam: [0.55, 0.35], flood: [0.6, 0.72], scribe: [0.2, 0.55], panic: [0.5, 0.2] },
  s18_ch2_crest: { hero: [0.36, 0.42], tower_base: [0.5, 0.82], smiley: [0.85, 0.2], lonely_noun: [0.45, 0.66] },
  s19_tribunal: { tribunal: [0.72, 0.33], hero: [0.35, 0.62], jury: [0.82, 0.42], solemn: [0.5, 0.2] },
  s20_wax_jury: { jury: [0.12, 0.5], scar_evidence: [0.78, 0.3], villain: [0.52, 0.36], drip: [0.65, 0.62] },
  s21_keepsake_oath: { keepsakes: [0.5, 0.7], hero: [0.25, 0.6], judge: [0.7, 0.45], objection: [0.5, 0.28] },
  s22_vell_appeal: { villain: [0.58, 0.3], hygiene: [0.86, 0.15], hero: [0.24, 0.52], appeal: [0.6, 0.45] },
  s23_storm: { storm: [0.5, 0.2], ink_rain: [0.7, 0.4], sacrifice: [0.6, 0.6], bard: [0.33, 0.44] },
  s24_three_wicks: { macguffin: [0.5, 0.45], hero: [0.33, 0.6], wicks: [0.5, 0.36], silence: [0.5, 0.2] },
  s25_last_paragraph: { narrator: [0.6, 0.15], hero: [0.3, 0.4], finale: [0.55, 0.5], dim: [0.13, 0.65] },
  s26_monologue_fight: { monologue: [0.55, 0.28], footnote: [0.4, 0.5], narrator: [0.61, 0.33], crowd: [0.25, 0.85] },
  s27_epilogue: { epilogue: [0.35, 0.65], narrator: [0.6, 0.3], scar_hint: [0.68, 0.65], sequel: [0.4, 0.25] },
};

/** Painted layer each anchor sits on (0 backdrop .. 3 nearest), measured from the layer alphas by
 * scripts/art: lets the pin and the card follow camera parallax at the right depth. */
const LAYER: Record<string, Record<string, number>> = {
  s01_gate: { hero: 2, gatekeeper: 1, whisper_gate: 1, candle_smoke: 1 },
  s02_archive: { archive: 0, villain: 1, macguffin: 1, trade: 0 },
  s03_bard: { bard: 1, stair: 1, ballad: 0, tower_mood: 0 },
  s04_tower: { tower: 0, hero: 1, windows: 0, climb: 2 },
  s05_market: { market: 0, extras: 1, riot: 1, noise: 0 },
  s06_bridge: { bridge: 1, chasm: 0, macguffin: 1, choice: 1 },
  s07_throne: { throne: 0, villain: 1, macguffin: 0, coronation: 0 },
  s08_reveal: { hero: 2, strings: 1, glitch: 0, panic: 0 },
  s09_oath_protocol: { oath: 0, hero: 1, gatekeeper: 1, protocol_mood: 0 },
  s10_tide_lock: { tide: 0, scriptorium: 0, scribe: 0, damp: 1 },
  s11_flood_shelves: { shelves: 0, hero: 1, prophecy_cards: 0, chapter_title: 0 },
  s12_scribe_pell: { scribe: 1, ledger: 2, hero: 2, ink_bribe: 1 },
  s13_margin_knight: { knight: 2, margin_road: 1, hero: 2, spacing: 1 },
  s14_ledger_trial: { trial: 1, villain: 1, macguffin: 1, objection: 0 },
  s15_bard_reprise: { bard: 1, undertow: 1, hero: 0, romantic_mood: 0 },
  s16_ink_whale: { whale: 1, macguffin: 0, hero: 2, song: 0 },
  s17_dam_burst: { dam: 1, flood: 1, scribe: 2, panic: 0 },
  s18_ch2_crest: { hero: 1, tower_base: 2, smiley: 0, lonely_noun: 1 },
  s19_tribunal: { tribunal: 0, hero: 1, jury: 1, solemn: 0 },
  s20_wax_jury: { jury: 2, scar_evidence: 1, villain: 1, drip: 2 },
  s21_keepsake_oath: { keepsakes: 1, hero: 1, judge: 1, objection: 0 },
  s22_vell_appeal: { villain: 1, hygiene: 0, hero: 1, appeal: 1 },
  s23_storm: { storm: 0, ink_rain: 1, sacrifice: 1, bard: 2 },
  s24_three_wicks: { macguffin: 0, hero: 1, wicks: 0, silence: 0 },
  s25_last_paragraph: { narrator: 0, hero: 1, finale: 0, dim: 1 },
  s26_monologue_fight: { monologue: 1, footnote: 0, narrator: 1, crowd: 2 },
  s27_epilogue: { epilogue: 2, narrator: 0, scar_hint: 2, sequel: 0 },
};

export function targetLayer(sceneId: string, elementId: string): number {
  const fig = figureAnchor(sceneId, elementId);
  if (fig) return fig.layer;
  return LAYER[sceneId]?.[elementId] ?? 2;
}

const BY_TYPE: Record<BeatType, Uv> = {
  character: [0.5, 0.5],
  place: [0.5, 0.4],
  object: [0.5, 0.58],
  event: [0.5, 0.55],
  mood: [0.5, 0.28],
};

export function targetUv(sceneId: string, elementId: string, beatType: BeatType): Uv {
  const fig = figureAnchor(sceneId, elementId);
  if (fig) return fig.uv;
  return A[sceneId]?.[elementId] ?? BY_TYPE[beatType] ?? [0.5, 0.5];
}

/**
 * Plate uv to a screen fraction inside the stage. The layers cover the frame with 15% overscan and
 * IA = 1.5 plates, so a point moves away from the center by that ratio. Camera drift is ignored (small).
 */
export function uvToScreen([u, v]: Uv, aspect: number): Uv {
  const ratioH = Math.max(1, aspect / 1.5) * 1.15;
  const ratioW = (ratioH * 1.5) / aspect;
  const x = 0.5 + (u - 0.5) * ratioW;
  const y = 0.5 + (v - 0.5) * ratioH;
  return [Math.min(0.94, Math.max(0.06, x)), Math.min(0.9, Math.max(0.1, y))];
}
